/**
 * Shared renderer for RFQ rich-text fields (description, selection_criteria,
 * about_company/team/project). These are stored as loosely-formed HTML
 * (sometimes with stray markdown-style headers/bullets mixed in from how
 * they were pasted in), so every page displaying them needs the same
 * clean-up pass - this was previously duplicated only in PublicJobListing.jsx.
 *
 * Callers are responsible for sanitizing the result (e.g. DOMPurify.sanitize)
 * before using it in dangerouslySetInnerHTML.
 */

// Merge consecutive <ol>/<ul> siblings so numbered lists render sequentially.
function mergeConsecutiveLists(htmlString) {
  if (typeof window === 'undefined' || !window.DOMParser) return htmlString;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    ['OL', 'UL'].forEach((tag) => {
      doc.querySelectorAll(tag).forEach((el) => {
        let next = el.nextElementSibling;
        while (next && next.tagName === tag) {
          const nextNext = next.nextElementSibling;
          while (next.firstChild) el.appendChild(next.firstChild);
          next.parentNode.removeChild(next);
          next = nextNext;
        }
      });
    });
    return doc.body.innerHTML;
  } catch (e) {
    return htmlString;
  }
}

export const fixSelectionCriteriaNumbering = (html) => {
  let counter = 0;
  return html.replace(/<ol[^>]*>/g, () => {
    counter++;
    return `<ol start="${counter}">`;
  });
};

export const cleanHTML = (raw) => {
  if (!raw) return '';
  let cleaned = String(raw).trim();

  cleaned = cleaned
    .replace(/^"{1,2}\s*$/gm, '')
    .replace(/^"{1,2}/, '')
    .replace(/"{1,2}$/, '');

  cleaned = cleaned
    .replace(/\[\s*['"]/g, '')
    .replace(/['"]\s*\]/g, '');

  cleaned = cleaned
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '    ')
    .replace(/\\u00a0/gi, ' ')
    .replace(/\\xa0/gi, ' ')
    .replace(/\\u202f/gi, ' ')
    .replace(/ /g, ' ')
    .replace(/ /g, ' ')
    .replace(/[ \t]+$/gm, '');

  cleaned = cleaned.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  cleaned = cleaned.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  cleaned = cleaned.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  cleaned = cleaned.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');
  cleaned = cleaned.replace(/^([A-Z][A-Za-z\s]{1,60}):\s*$/gm, '<h3>$1</h3>');
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  const lines = cleaned.split('\n');
  const processed = [];
  let inList = null;

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed === '') {
      if (!inList) processed.push('');
      continue;
    }

    const bulletMatch = /^[\s]*[-*•·]\s+(.+)$/.exec(line);
    const numberMatch = /^[\s]*\d+[.)]\s+(.+)$/.exec(line);
    const isBullet = !!bulletMatch;
    const isNumbered = !!numberMatch;
    const isListItem = isBullet || isNumbered;
    const listType = isNumbered ? 'ol' : isBullet ? 'ul' : null;

    if (isListItem && inList !== listType) {
      if (inList) processed.push(`</${inList}>`);
      inList = listType;
      processed.push(`<${inList}>`);
    } else if (!isListItem && inList) {
      processed.push(`</${inList}>`);
      inList = null;
    }

    if (isListItem) {
      const content = (isBullet ? bulletMatch[1] : numberMatch[1]).trim();
      processed.push(`<li>${content}</li>`);
    } else {
      processed.push(line);
    }
  }

  if (inList) processed.push(`</${inList}>`);
  cleaned = processed.join('\n');

  cleaned = cleaned
    .replace(/(<li[^>]*>\s*)(\d+[.)]\s*)/g, '$1')
    .replace(/(<li[^>]*>\s*<p[^>]*>)\s*\d+[.)]\s*/g, '$1');

  cleaned = mergeConsecutiveLists(cleaned);

  cleaned = cleaned
    .split(/\n\s*\n/)
    .map((block) => {
      const t = block.trim();
      if (!t) return '';
      if (/^<(h[1-6]|p|ul|ol|li|div|table|blockquote)\b/i.test(t)) return t;
      return `<p>${t}</p>`;
    })
    .join('\n\n');

  return cleaned;
};

export const RICH_TEXT_PROSE_CLASSES = 'prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1 [&_p]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-4 [&_strong]:font-semibold text-gray-700';
